import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type KeyboardEvent,
} from 'react';
import { Box, Flex } from '@chakra-ui/react';
import { Button, Tag, Text } from '@kula-ai/components';
import { Attachment02, Send01, Stop, XClose, File05 } from '@kula-ai/icons-core';
import { useAgentForgeStore } from '../../stores';
import { useStopTurn } from '../../hooks/useStopTurn';
import { MentionEditor, type MentionEditorHandle } from './MentionEditor';

const DISCLAIMER = 'Kula AI can make mistakes. Please double-check responses.';
// Max bytes per file accepted by the picker. Keeps the prompt-prefix path
// honest about what we'll inline. Lift this once a real upload endpoint
// lands and large files can stream up.
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

// Skill slash commands — typing "/" surfaces these; selecting inserts the
// trigger and the backend's tier-0 router does the rest. Static registry:
// future skills add one entry.
const SLASH_COMMANDS = [
  {
    cmd: '/marketmap',
    args: '[role, location]',
    hint: 'Map the talent market and build a sourcing plan',
  },
  {
    cmd: '/salarybenchmark',
    args: '[role, location]',
    hint: 'Benchmark market compensation for a role',
  },
];

interface ComposerProps {
  placeholder?: string;
  /**
   * `false` (default) — single-line editor with attach + send embedded
   * to the right. Used by the floating panel and fullscreen.
   * `true` — multi-line editor with attach + send pinned beneath. Used
   * by the route-level empty state.
   */
  multiline?: boolean;
  /**
   * When `true`, the previous turn is still resolving — paperclip + send
   * are disabled and Enter no-ops, but the input itself stays editable so
   * the user can keep drafting their follow-up while the agent thinks
   * (matches ChatGPT/Claude). Text already typed is preserved.
   */
  isAwaiting?: boolean;
  /**
   * Called when the user submits non-whitespace input. Composer clears
   * its own editor before invoking, so the caller never sees the stale
   * text. The string is the server-format payload — mentions appear as
   * `<tag id="X" entity="Y">name</tag>` markers in the otherwise-plain
   * text. Attached files are passed as the second argument so the caller can
   * upload them (multipart) rather than smuggling their names into the text.
   */
  onSubmit?: (_text: string, _files?: File[]) => void;
  /**
   * Focus the underlying editor on mount. Mount-only — flipping this
   * later does not re-focus, and successful sends don't refocus either
   * (the editor never blurred). Used by ThreadContainer's Footer so
   * opening a chat thread lands the cursor in the composer.
   */
  autoFocus?: boolean;
  /**
   * Pass-through key handler invoked before Composer's internal
   * Enter / Shift+Enter logic. Call `event.preventDefault()` to
   * suppress the built-in submit-on-Enter for that keystroke.
   */
  onKeyType?: (_event: KeyboardEvent<HTMLElement>) => void;
  /**
   * Fires when the composer flips between empty and non-empty (text OR
   * attachments). The landing surface uses it to fade its background
   * illustration once the user starts drafting.
   */
  onContentChange?: (_hasContent: boolean) => void;
}

interface ActionsProps {
  canSend: boolean;
  disabled?: boolean;
  onSend: () => void;
  onAttach: () => void;
  /** True while a turn is in flight — the send slot becomes a Stop button. */
  awaiting?: boolean;
  onStop?: () => void;
  stopping?: boolean;
}

interface AttachmentChipsProps {
  files: File[];
  onRemove: (_index: number) => void;
}

const AttachmentChips = ({ files, onRemove }: AttachmentChipsProps) => {
  if (files.length === 0) return null;
  return (
    <Flex wrap="wrap" gap="6px">
      {files.map((file, idx) => (
        <Tag
          key={`${file.name}-${idx}`}
          variant="white"
          size="md"
          label={file.name}
          leftElement={<File05 size={16} />}
          rightElement={
            <Box
              as="button"
              type="button"
              aria-label={`Remove ${file.name}`}
              onClick={() => onRemove(idx)}
              display="inline-flex"
              cursor="pointer"
            >
              <XClose size={16} />
            </Box>
          }
        />
      ))}
    </Flex>
  );
};

function ComposerActions({
  canSend,
  disabled = false,
  onSend,
  onAttach,
  awaiting = false,
  onStop,
  stopping = false,
}: ActionsProps): JSX.Element {
  return (
    <Box display="flex" alignItems="center" gap="4px">
      <Button
        size="icon"
        variant="quaternary"
        aria-label="Attach file"
        className="text-secondary-700 !border-0"
        disabled={disabled}
        onClick={onAttach}
      >
        <Attachment02 size={16} color="currentColor" />
      </Button>
      {awaiting && onStop ? (
        // Claude-style Stop: while the agent works, the send slot becomes
        // a stop control — cancelling must never cost more than a click.
        <Button
          size="icon"
          aria-label="Stop generating"
          disabled={stopping}
          onClick={onStop}
          className="bg-secondary-900 text-base-white hover:bg-secondary-700"
          data-test-id="composer-stop"
        >
          <Stop size={16} color="currentColor" />
        </Button>
      ) : (
        <Button
          size="icon"
          aria-label="Send"
          disabled={!canSend || disabled}
          onClick={onSend}
          className="bg-secondary-900 text-base-white disabled:bg-secondary-50 disabled:text-secondary-400 hover:bg-secondary-900"
        >
          <Send01 size={16} color="currentColor" />
        </Button>
      )}
    </Box>
  );
}

export const Composer = ({
  placeholder = 'Ask Kula...',
  multiline = false,
  isAwaiting = false,
  autoFocus = false,
  onSubmit,
  onKeyType,
  onContentChange,
}: ComposerProps) => {
  const editorRef = useRef<MentionEditorHandle>(null);
  const [plainText, setPlainText] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  // Names of files rejected by the size cap on the LAST pick — silent drops
  // made users think the attachment went through.
  const [rejectedNames, setRejectedNames] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { stop: stopTurn, stopping } = useStopTurn();

  // Per-session draft: restore on mount (ThreadContainer remounts the
  // composer per session), autosave debounced while typing, clear on send.
  // The serialized form is saved so mention chips survive a reload.
  const sessionIdAtMount = useRef(
    useAgentForgeStore.getState().activeSessionId
  );
  const initialDraft = useRef(
    sessionIdAtMount.current
      ? (useAgentForgeStore.getState().draftBySession[
          sessionIdAtMount.current
        ] ?? '')
      : ''
  );
  const setSessionDraft = useAgentForgeStore((s) => s.setSessionDraft);
  const draftTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleDraftSave = useCallback(() => {
    const sid = sessionIdAtMount.current;
    if (!sid) return;
    if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
    draftTimerRef.current = setTimeout(() => {
      setSessionDraft(sid, editorRef.current?.getSerialized() ?? '');
    }, 400);
  }, [setSessionDraft]);
  // Flush the pending save on unmount so a fast session switch keeps the
  // last keystrokes.
  useEffect(
    () => () => {
      if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
      const sid = sessionIdAtMount.current;
      if (sid && editorRef.current) {
        setSessionDraft(sid, editorRef.current.getSerialized());
      }
    },
    [setSessionDraft]
  );
  const slashQuery =
    plainText.startsWith('/') && !plainText.includes(' ')
      ? plainText.toLowerCase()
      : null;
  const slashMatches = useMemo(
    () =>
      slashQuery !== null
        ? SLASH_COMMANDS.filter((c) => c.cmd.startsWith(slashQuery))
        : [],
    [slashQuery]
  );
  // Keyboard-driven autocomplete state: the first match is pre-selected,
  // arrows move the highlight, Enter/Tab complete it, Escape dismisses the
  // popover until the query changes. Index resets whenever the query does.
  const [slashIndex, setSlashIndex] = useState(0);
  const [slashDismissed, setSlashDismissed] = useState(false);
  useEffect(() => {
    setSlashIndex(0);
    setSlashDismissed(false);
  }, [slashQuery]);
  const slashOpen =
    !slashDismissed && slashQuery !== null && slashMatches.length > 0;
  const activeSlashIndex = Math.min(
    slashIndex,
    Math.max(slashMatches.length - 1, 0)
  );
  const applySlashCommand = useCallback((cmd: string) => {
    editorRef.current?.setText(cmd + ' ');
  }, []);

  const handleEditorChange = useCallback(
    (text: string) => {
      setPlainText(text);
      scheduleDraftSave();
    },
    [scheduleDraftSave]
  );

  const trimmed = plainText.trim();
  // Allow send when there's text OR attachments — uploading just a file
  // (no prompt) is a valid intent the caller can interpret.
  const canSend = trimmed.length > 0 || files.length > 0;

  // Notify the caller when content presence flips (empty ↔ has text/files).
  useEffect(() => {
    onContentChange?.(canSend);
  }, [canSend, onContentChange]);

  const handleSend = useCallback(() => {
    if (!canSend) return;
    // Attachments can't be queued behind a running turn — the File handles
    // don't survive; text-only submits queue via useChatSubmit instead.
    if (isAwaiting && files.length > 0) return;
    const serialized = editorRef.current?.getSerialized().trim() ?? '';
    const attached = files;
    editorRef.current?.clear();
    setPlainText('');
    setFiles([]);
    const sid = sessionIdAtMount.current;
    if (sid) {
      if (draftTimerRef.current) clearTimeout(draftTimerRef.current);
      setSessionDraft(sid, '');
    }
    onSubmit?.(serialized, attached);
  }, [canSend, files, isAwaiting, onSubmit, setSessionDraft]);

  // Submit on Enter; Shift+Enter inserts a newline (multiline only).
  // While a turn is running, Enter still submits — useChatSubmit queues the
  // message and sends it when the reply finishes (ChatGPT-style), so a fast
  // follow-up thought is never swallowed.
  //
  // The external `onKeyType` runs first so it can `preventDefault` to
  // suppress the built-in submit-on-Enter when the caller wants to
  // intercept the keystroke.
  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLElement>) => {
      onKeyType?.(event);
      if (event.defaultPrevented) return;
      // Slash popover navigation: arrows move the highlight, Tab completes,
      // Escape dismisses. Handled before the Enter logic so the popover owns
      // these keys while it's open.
      if (slashOpen) {
        switch (event.key) {
          case 'ArrowDown':
            event.preventDefault();
            setSlashIndex((i) => (i + 1) % slashMatches.length);
            return;
          case 'ArrowUp':
            event.preventDefault();
            setSlashIndex(
              (i) => (i - 1 + slashMatches.length) % slashMatches.length
            );
            return;
          case 'Tab':
            event.preventDefault();
            applySlashCommand(slashMatches[activeSlashIndex].cmd);
            return;
          case 'Escape':
            event.preventDefault();
            setSlashDismissed(true);
            return;
          default:
        }
      }
      if (event.key !== 'Enter') return;
      if (multiline && event.shiftKey) return;
      event.preventDefault();
      // Slash popover open on a PARTIAL command: Enter completes the
      // highlighted match instead of sending the fragment — "/mar" + Enter
      // once went out as a real message and became a bogus search. A fully
      // typed command still sends.
      if (slashOpen && !slashMatches.some((c) => c.cmd === slashQuery)) {
        applySlashCommand(slashMatches[activeSlashIndex].cmd);
        return;
      }
      handleSend();
    },
    [
      handleSend,
      multiline,
      onKeyType,
      slashOpen,
      slashQuery,
      slashMatches,
      activeSlashIndex,
      applySlashCommand,
    ]
  );

  // One popover element shared by the multiline and single-line branches.
  // The highlighted row tracks activeSlashIndex (keyboard) and mouse hover;
  // click and Enter/Tab both complete through applySlashCommand.
  const slashPopover = slashOpen ? (
    <Box
      border="1px solid"
      borderColor="secondary-200"
      borderRadius="10px"
      bg="base-white"
      mb="6px"
      overflow="hidden"
      boxShadow="0 4px 12px rgba(16,24,40,0.08)"
      data-test-id="composer-slash-popover"
    >
      {slashMatches.map((c, i) => (
        <Box
          key={c.cmd}
          px="12px"
          py="8px"
          cursor="pointer"
          bg={i === activeSlashIndex ? 'secondary-50' : undefined}
          onMouseEnter={() => setSlashIndex(i)}
          onClick={() => applySlashCommand(c.cmd)}
          data-test-id={`composer-slash-${c.cmd.slice(1)}`}
          data-active={i === activeSlashIndex || undefined}
        >
          <Text variant="span" className="text-secondary-900 font-medium">
            {c.cmd}{' '}
            <Text variant="small" as="span" className="text-secondary-400">
              {c.args}
            </Text>
          </Text>
          <Text
            variant="small"
            className="text-secondary-500"
            style={{ display: 'block' }}
          >
            {c.hint}
          </Text>
        </Box>
      ))}
    </Box>
  ) : null;

  const handleAttachClick = () => {
    fileInputRef.current?.click();
  };

  const handleFilesPicked = (event: ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(event.target.files ?? []);
    // Oversized files can't be attached (10 MB cap) — say so instead of
    // dropping them silently, which read as a successful attach.
    const valid = picked.filter((f) => f.size <= MAX_FILE_SIZE_BYTES);
    const rejected = picked.filter((f) => f.size > MAX_FILE_SIZE_BYTES);
    setRejectedNames(rejected.map((f) => f.name));
    setFiles((prev) => [...prev, ...valid]);
    // Reset so the same file can be re-picked after removal.
    event.target.value = '';
  };

  const handleRemoveFile = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const rejectedNote =
    rejectedNames.length > 0 ? (
      <Text
        variant="small"
        className="text-error-600 mt-1"
        data-test-id="composer-file-rejected"
      >
        {rejectedNames.join(', ')}{' '}
        {rejectedNames.length === 1
          ? 'is over the 10 MB limit and wasn\u2019t attached.'
          : 'are over the 10 MB limit and weren\u2019t attached.'}
      </Text>
    ) : null;

  const hiddenFileInput = (
    <input
      ref={fileInputRef}
      type="file"
      multiple
      hidden
      onChange={handleFilesPicked}
      data-test-id="composer-file-input"
    />
  );

  if (multiline) {
    return (
      <Box>
        <Box
          data-mention-input-container
          bg="base-white"
          border="1px solid"
          borderColor="secondary-100"
          borderRadius="12px"
          px="10px"
          py="12px"
        >
          {rejectedNote}
          {slashPopover}
          <MentionEditor
            ref={editorRef}
            multiline
            autoFocus={autoFocus}
            placeholder={placeholder}
            initialValue={initialDraft.current}
            onChange={handleEditorChange}
            onKeyDown={handleKeyDown}
            minHeight={56}
            maxHeight={160}
            testId="composer-input"
          />
          <Flex align="center" justify="space-between" gap="8px" mt="6px">
            <Box flex="1" minW="0">
              <AttachmentChips files={files} onRemove={handleRemoveFile} />
            </Box>
            <ComposerActions
              canSend={canSend}
              disabled={isAwaiting}
              onSend={handleSend}
              onAttach={handleAttachClick}
            />
          </Flex>
        </Box>
        {hiddenFileInput}
      </Box>
    );
  }

  return (
    <Box>
      {files.length > 0 && (
        <Box mb="8px">
          <AttachmentChips files={files} onRemove={handleRemoveFile} />
        </Box>
      )}
      {rejectedNote}
      {slashPopover}
      <Flex
        data-mention-input-container
        align="center"
        gap="8px"
        bg="base-white"
        border="1px solid"
        borderColor="secondary-100"
        borderRadius="12px"
        px="12px"
        py="8px"
      >
        <Box flex="1" minW="0">
          <MentionEditor
            ref={editorRef}
            autoFocus={autoFocus}
            placeholder={placeholder}
            initialValue={initialDraft.current}
            onChange={handleEditorChange}
            onKeyDown={handleKeyDown}
            minHeight={24}
            testId="composer-input"
          />
        </Box>
        <ComposerActions
          canSend={canSend}
          disabled={isAwaiting}
          onSend={handleSend}
          onAttach={handleAttachClick}
          awaiting={isAwaiting}
          onStop={() => void stopTurn()}
          stopping={stopping}
        />
      </Flex>
      {hiddenFileInput}
      <Text className="text-center mt-2 text-secondary-400 font-[450] text-[12px]">
        {DISCLAIMER}
      </Text>
    </Box>
  );
};
